import { useCallback, useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@common-fe/common-fe';
import dayjs from 'dayjs';
import { toString } from 'lodash';

import { api } from '@/api';
import { RAW_DATE_FORMAT } from '@/common/constants';
import PATHS from '@/common/paths';
import { GET_DEPENDENTS_BY_EMPLOYEE_ID_KEY } from '@/modules/user/components/ManageProfile/components/Dependents/queries/useDependents.query';
import { useAuthStore } from '@/modules/user/stores';
import { UserModel } from '@/modules/user/user.types';
import { formatDependentSsnToServer } from '@/utils';

export interface Patient {
  id: number;
  firstName: string;
  lastName: string;
  ssn?: string;
  relationshipType: string;
  birthOn: string;
  fullName: string;
}

export interface PatientPayload {
  dependent_id: number;
  first_name: string;
  last_name: string;
  relationship_type: string;
  birth_on: string;
}

export interface Person {
  id: number;
  preferredName: string;
  firstName: string;
  lastName: string;
}

const CREATE_DEPENDENT_KEY = 'update_card_holder';
const CREATION_SOURCE_TYPE = 'WEB_PARTICIPANT_PORTAL';

const formatPatientToServer = (patient: Patient, user?: UserModel | null) => ({
  ...formatDependentSsnToServer(patient.ssn),
  first_name: patient.firstName,
  last_name: patient.lastName,
  birth_on: dayjs(patient.birthOn).format(RAW_DATE_FORMAT),
  person: {
    id: user?.employee?.personId,
    preferred_name: user?.preferredName,
    first_name: user?.firstName,
    last_name: user?.lastName,
  },
  employee_id: user?.employee?.id,
  relationship_type: patient.relationshipType,
  creation_source_type: CREATION_SOURCE_TYPE,
});

export const useCreatePatientQuery = (onSuccess?: (dependent?: PatientPayload) => void) => {
  const query = useMemo(() => {
    const queryGenerator = new URLSearchParams();

    queryGenerator.set(CREATE_DEPENDENT_KEY, `${false}`);

    return queryGenerator;
  }, []);
  const { user } = useAuthStore();
  const queryClient = useQueryClient();
  const { mutateAsync, isLoading } = useMutation(
    (value: Patient) => api.post(`${PATHS.CREATE_DEPENDENT}?${query}`, formatPatientToServer(value, user)),
    {
      onSuccess: (newData) => {
        queryClient.invalidateQueries({ queryKey: [GET_DEPENDENTS_BY_EMPLOYEE_ID_KEY] });
        if (onSuccess) {
          onSuccess(newData?.data as PatientPayload);
        }
      },
    }
  );

  const handleSave = useCallback(
    async (value: Patient) => {
      const response = await mutateAsync(value);
      return response?.data as PatientPayload;
    },
    [mutateAsync]
  );

  return {
    isLoading,
    save: handleSave,
  };
};

export const useGetPatientsQuery = () => {
  const { user } = useAuthStore();
  const { isLoading, isSuccess, isError, data } = useQuery(
    [GET_DEPENDENTS_BY_EMPLOYEE_ID_KEY, user?.employee?.id],
    () => api.get(PATHS.GET_DEPENDENTS_BY_EMPLOYEE_ID(user?.employee?.id), {
        headers: {
          'X-DETAILS-RESOLVERS': 'DEPENDENT',
        },
      }),
    { enabled: Boolean(user?.employee?.id) }
  );
  return {
    patients:
      data?.data?.map((patient: PatientPayload) => ({
        id: toString(patient.dependent_id),
        firstName: patient.first_name,
        lastName: patient.last_name,
        fullName: `${patient.first_name || ''} ${patient.last_name || ''}`.trim(),
        relationshipType: patient.relationship_type,
        birthOn: patient.birth_on,
      })) || [],
    isError,
    isLoading,
    isSuccess,
  };
};
